/* eslint-disable jsx-a11y/no-interactive-element-to-noninteractive-role */
import React from 'react';

import { IPlansTableFields } from '../../../types/contentful';

import { CFColorToken } from '../../theme/tokens/contentful-color';

import { Plan } from '../Plan';
import RichText from '../RichText';
import { Text } from '../Text';
import { Box } from '../Box';
import { BlockContainer, Layout, BoxColumn } from '../Layout';
import PlansIcon from './PlansIcon';
import { Features, processPlans } from './helper';

import {
  MobileVersion,
  DesktopVersion,
  Table,
  TableTitle,
  TableHead,
  PlanText,
  FeatureRow,
  BufferCell,
  FeatureCell,
  Checkmark
} from './styles';

export function PlansTable({
  title,
  desktopTitle,
  plans = []
}: IPlansTableFields) {
  if (!plans) {
    return <div />;
  }

  const allFeatures: Features = processPlans(plans);

  return (
    <BlockContainer
      marginTop={{ 0: 'blockMarginY', tablet: 'blockPadding_tablet' }}
      marginBottom={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
      data-blockid="plansTable"
    >
      <Layout>
        <BoxColumn
          size={{ 0: 12, desktop: 10 }}
          paddingTop={{ 0: 0, desktop: 'xs' }}
        >
          <MobileVersion
            data-blockid="plans-table-mobile"
            data-testid="plans-table-mobile"
          >
            <Text
              as="h2"
              fontWeight={{ 0: 'h2' }}
              fontSize={{ 0: 'h2_0', desktop: 'h2_desktop' }}
              data-blockid="plansTable-desktop-title"
              paddingLeft="m"
            >
              {title}
            </Text>
            {(plans || []).map(({ sys, fields }, idx: number) => (
              <Plan
                {...fields}
                key={sys?.id}
                features={allFeatures.filter(
                  (feature) =>
                    feature.includedInNumberOfPlans >= plans.length - idx
                )}
              />
            ))}
          </MobileVersion>
          <DesktopVersion
            data-blockid="plans-table-desktop"
            data-testid="plans-table-desktop"
          >
            <Table>
              <thead>
                <tr>
                  <TableTitle>
                    <Text
                      as="h2"
                      fontWeight={{ 0: 'h2' }}
                      fontSize={{ 0: 'h2_0', desktop: 'h2_desktop' }}
                      data-blockid="plansTable-desktop-title"
                      textAlign="left"
                    >
                      {desktopTitle}
                    </Text>
                  </TableTitle>
                  <th role="presentation" />
                  {(plans || []).map(({ sys, fields }) => (
                    <React.Fragment key={sys?.id}>
                      <th role="presentation" />
                      <TableHead key={sys?.id} color={fields?.color ?? ''}>
                        <PlanText
                          as="h3"
                          fontWeight="h3"
                          fontSize="h3_tablet"
                          data-blockid="plansTable-desktop-title"
                          color={fields?.textColor ?? ('black' as CFColorToken)}
                        >
                          {fields?.name}
                        </PlanText>
                        <PlanText
                          as="p"
                          fontSize="m"
                          fontWeight="h3"
                          data-blockid="plansTable-desktop-info"
                          color={fields?.textColor ?? ('black' as CFColorToken)}
                          paddingTop="sm"
                        >
                          {fields?.subheader}
                        </PlanText>
                      </TableHead>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {allFeatures.map((feature, index: number) => (
                  <FeatureRow key={index}>
                    <FeatureCell>
                      <Text as="h3" fontWeight="h3" fontSize="h3_tablet">
                        {feature && feature.title}
                      </Text>
                      <Box paddingTop="sm">
                        {feature?.info && <RichText data={feature.info} />}
                      </Box>
                    </FeatureCell>
                    <BufferCell role="presentation" />
                    {plans?.map(({ fields }, planIndex: number) => {
                      const isInPlan =
                        feature.includedInNumberOfPlans >=
                        plans.length - planIndex;
                      const nextFeatureIsNotInPlan =
                        allFeatures[index + 1]?.includedInNumberOfPlans <
                        plans.length - planIndex;
                      const featureCopy = feature.copies.get(fields.name ?? '');

                      return (
                        <React.Fragment key={`${index}-${planIndex}`}>
                          <BufferCell role="presentation" />
                          <Checkmark
                            color={
                              isInPlan
                                ? fields?.color || ('white' as CFColorToken)
                                : ('white' as CFColorToken)
                            }
                            lastFeatureInPlan={
                              (isInPlan && nextFeatureIsNotInPlan) ||
                              !allFeatures[index + 1]
                            }
                          >
                            {isInPlan ? (
                              <PlansIcon
                                name={fields?.name as string}
                                title={feature?.title as string}
                                textColor={fields?.textColor as CFColorToken}
                                replaceWithText={featureCopy}
                              />
                            ) : (
                              ''
                            )}
                          </Checkmark>
                        </React.Fragment>
                      );
                    })}
                  </FeatureRow>
                ))}
              </tbody>
            </Table>
          </DesktopVersion>
        </BoxColumn>
      </Layout>
    </BlockContainer>
  );
}
